import { authClient } from "./client.js";

export interface AuthUser {
  id: string;
  email: string;
  name?: string;
  image?: string | null;
  emailVerified: boolean;
}

/**
 * Thin wrapper over better-auth's `useSession` plus our magic-link callsite.
 *
 * `signInWithEmail(email)` sends a magic link to that address; the user
 * clicks it in their email and the cookie session gets established. The
 * `callbackURL` keeps them on the home page after verification — must be
 * an absolute URL because in prod the auth client's `baseURL` points at
 * the API origin, so a relative `/` would resolve there instead of here.
 */
const frontendHome = (): string =>
  typeof window !== "undefined" ? `${window.location.origin}/` : "/";

export function useAuth() {
  const sessionQuery = authClient.useSession();
  const data = sessionQuery.data as { user?: AuthUser } | null | undefined;
  const user = data?.user ?? null;

  return {
    isLoading: sessionQuery.isPending,
    isAuthenticated: !!user,
    user,
    signInWithEmail: (email: string) =>
      authClient.signIn.magicLink({ email, callbackURL: frontendHome() }),
    signInWithGoogle: () =>
      authClient.signIn.social({ provider: "google", callbackURL: frontendHome() }),
    signInWithGitHub: () =>
      authClient.signIn.social({ provider: "github", callbackURL: frontendHome() }),
    signOut: async () => {
      await authClient.signOut();
    },
  };
}
